import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import axios from 'axios';
import config from '../config/config.json';
import {
  CreateDynamicPickitPackRequest,
  DeleteDynamicPickitPackResponse,
  DynamicPickitPack,
  UpdateDynamicPickitPackRequest,
} from '../types/dynamic-pickit';

const API_BASE = 'api/dynamic-pickit';

export function useDynamicPickitPacks() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: packs = [], isLoading } = useQuery<DynamicPickitPack[]>({
    queryKey: ['dynamicPickitPacks'],
    queryFn: async () => {
      const response = await axios.get(`${config.backend_base_url}${API_BASE}/packs`);
      return response.data;
    },
  });

  const { mutate: createPack } = useMutation({
    mutationFn: async (data: CreateDynamicPickitPackRequest) => {
      const response = await axios.post(`${config.backend_base_url}${API_BASE}/packs`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dynamicPickitPacks'] });
      toast.success(t('staff.packCreated'));
    },
    onError: () => {
      toast.error(t('staff.packCreateError'));
    },
  });

  const { mutate: updatePack } = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateDynamicPickitPackRequest }) => {
      const response = await axios.put(`${config.backend_base_url}${API_BASE}/packs/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dynamicPickitPacks'] });
      toast.success(t('staff.packUpdated'));
    },
    onError: () => {
      toast.error(t('staff.packUpdateError'));
    },
  });

  const { mutate: deletePack } = useMutation({
    mutationFn: async (id: number) => {
      const response = await axios.delete(`${config.backend_base_url}${API_BASE}/packs/${id}`);
      return response.data as DeleteDynamicPickitPackResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dynamicPickitPacks'] });
      toast.success(t('staff.packDeleted'));
    },
    onError: () => {
      toast.error(t('staff.packDeleteError'));
    },
  });

  const { mutate: uploadPacks } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${config.backend_base_url}${API_BASE}/packs/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dynamicPickitPacks'] });
      toast.success(t('staff.packsUploaded'));
    },
    onError: () => {
      toast.error(t('staff.packsUploadError'));
    },
  });

  return {
    packs,
    isLoading,
    createPack,
    updatePack,
    deletePack,
    uploadPacks,
  };
}
